import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { COLORS } from '../theme';
// Animations
const slideDown = keyframes`
    from {
        opacity: 0;
        transform: translateY(-20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const growLine = keyframes`
    from {
        height: 0;
    }
    to {
        height: 32px;
    }
`;

const spin = keyframes`
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
`;

// Styled Components
const ConfigCheckContainer = styled.div`
    margin-top: 24px;
    padding-left: 8px;
    position: relative;
`;

const CheckingText = styled.div`
    font-size: 14px;
    color: #6B7280;
    margin-bottom: 24px;
`;

const CheckList = styled.div`
    display: flex;
    flex-direction: column;
`;

const CheckItemContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    opacity: 0;
    animation: ${slideDown} 0.3s ease forwards;
    animation-delay: ${props => props.delay}s;
    position: relative;
`;

const IconContainer = styled.div`
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    z-index: 2;
    background: #fff;
`;

const LoadingSpinner = styled.div`
    width: 20px;
    height: 20px;
    border: 2px solid #D6DBF7;
    border-top: 2px solid #3249D7;
    border-radius: 50%;
    animation: ${spin} 1s linear infinite;
`;

const SuccessIcon = styled.div`
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background:rgb(30, 193, 95);
    display: flex;
    align-items: center;
    justify-content: center;
    
    &::after {
        content: '';
        width: 6px;
        height: 10px;
        border: solid white;
        border-width: 0 2px 2px 0;
        transform: rotate(45deg) translate(-1px, -1px);
    }
`;

const ErrorIcon = styled.div`
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #DC2626;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &::before,
    &::after {
        content: '';
        position: absolute;
        width: 12px;
        height: 2px;
        background: white;
    }
    
    &::before {
        transform: rotate(45deg);
    }
    
    &::after {
        transform: rotate(-45deg);
    }
`;

const CheckText = styled.div`
    font-size: 14px;
    color: ${props => props.status === 'success' ? '#3249D7' : 
                     props.status === 'error' ? '#DC2626' : '#6B7280'};
    transition: color 0.3s ease;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const RetryButton = styled.button`
    background: none;
    border: none;
    color: #3249D7;
    font-size: 12px;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    gap: 4px;
    
    &:hover {
        background: #F3F4F6;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const LineContainer = styled.div`
    height: 32px;
    margin-left: 11px;
    position: relative;
`;

const Line = styled.div`
    width: 1px;
    background: ${props => props.status === 'success' ? '#E6E7EA' : 
                         props.status === 'error' ? '#DC2626' : '#D6DBF7'};
    position: absolute;
    left: 0;
    top: 0;
    animation: ${growLine} 0.3s ease forwards;
    animation-delay: ${props => props.delay}s;
    opacity: ${props => props.visible ? 1 : 0};
    transition: background-color 0.3s ease;
`;

const CheckIcon = ({ status }) => {
    if (status === 'loading') return <LoadingSpinner />;
    if (status === 'success') return <SuccessIcon />;
    if (status === 'error') return <ErrorIcon />;
    return <LoadingSpinner />;
};

const CheckItem = ({ label, status, delay, isLast, visible, onRetry, isRetrying }) => (
    <>
        <CheckItemContainer delay={delay}>
            <IconContainer>
                <CheckIcon status={status} />
            </IconContainer>
            <CheckText status={status}>
                {label}
                {status === 'error' && (
                    <RetryButton onClick={onRetry} disabled={isRetrying}>
                        {isRetrying ? 'Checking...' : 'Try Again'}
                    </RetryButton>
                )}
            </CheckText>
        </CheckItemContainer>
        {!isLast && (
            <LineContainer>
                <Line 
                    status={status}
                    delay={delay + 0.2}
                    visible={visible}
                />
            </LineContainer>
        )}
    </>
);

const ConfigCheck = ({ onAllChecksComplete }) => {
    const [checkStatuses, setCheckStatuses] = useState({
        internet: 'pending',
        webcam: 'pending',
        audioInput: 'pending',
        microphone: 'pending',
        others: 'pending'
    });

    const [retrying, setRetrying] = useState({});
    const [visibleItems, setVisibleItems] = useState({});

    const checkItems = [
        { key: 'internet', label: 'Internet Connection' },
        { key: 'webcam', label: 'Camera Access' },
        { key: 'audioInput', label: 'Audio Input' },
        { key: 'microphone', label: 'Microphone Access' },
        { key: 'others', label: 'System Check' }
    ];

    const runCheck = async (key) => {
        setCheckStatuses(prev => ({ ...prev, [key]: 'loading' }));
        
        let result = false;
        switch(key) {
            case 'internet':
                result = navigator.onLine;
                break;
            case 'webcam':
                try {
                    const stream = await navigator.mediaDevices.getUserMedia({ video: true });
                    stream.getTracks().forEach(track => track.stop());
                    result = true;
                } catch (error) {
                    result = false;
                }
                break;
            case 'audioInput':
            case 'microphone':
                try {
                    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                    stream.getTracks().forEach(track => track.stop());
                    result = true;
                } catch (error) {
                    result = false;
                }
                break;
            case 'others':
                result = true;
                break;
        }

        setCheckStatuses(prev => ({ 
            ...prev, 
            [key]: result ? 'success' : 'error' 
        }));

        return result;
    };

    const handleRetry = async (key) => {
        setRetrying(prev => ({ ...prev, [key]: true }));
        await runCheck(key);
        setRetrying(prev => ({ ...prev, [key]: false }));
    };

    useEffect(() => {
        const runChecks = async () => {
            for (const item of checkItems) {
                setVisibleItems(prev => ({ ...prev, [item.key]: true }));
                await new Promise(resolve => setTimeout(resolve, 800));
                await runCheck(item.key);
            }
        };

        runChecks();
    }, []);

    useEffect(() => {
        const allSuccess = Object.values(checkStatuses).every(status => status === 'success');
        if (allSuccess) {
            onAllChecksComplete?.();
        }
    }, [checkStatuses, onAllChecksComplete]);

    return (
        <ConfigCheckContainer>
            <CheckingText>Checking system requirements...</CheckingText>
            <CheckList>
                {checkItems.map((item, index) => (
                    <CheckItem
                        key={item.key}
                        label={item.label}
                        status={checkStatuses[item.key]}
                        delay={index * 0.8}
                        isLast={index === checkItems.length - 1}
                        visible={visibleItems[item.key]}
                        onRetry={() => handleRetry(item.key)}
                        isRetrying={retrying[item.key]}
                    />
                ))}
            </CheckList>
        </ConfigCheckContainer>
    );
};

export default ConfigCheck;